import type { SettingEffect, SettingField, SettingSource } from '../../types/settings'

// Display arithmetic for the settings screen: which section a field belongs in, what to call a
// source, and how a list-shaped setting is written as one string. Pure — no React, no fetch. Nothing
// here decides anything: the API decides the value, the source and the effect; this file decides the
// order they are read in.

export interface SettingSection {
  id: string
  title: string
  blurb: string
  fields: SettingField[]
}

/** The sections, in the order an admin reads them: who we are, then what we read, then when. */
const SECTIONS: { id: string; title: string; blurb: string; keys: string[] }[] = [
  {
    id: 'orgs',
    title: 'Organisations',
    blurb: 'Each mailbox domain is an organisation inside this tenant. Two domains that are one firm fold together here, and what each org is called is the firm’s own word for itself.',
    keys: ['orgAliases', 'orgNames'],
  },
  {
    id: 'mail',
    title: 'Reading mail',
    blurb: 'How much of a mailbox a first read covers, and who counts as one of us when Scout reads a thread.',
    keys: ['lookbackMonths', 'firmDomains', 'publicMailDomains'],
  },
  {
    id: 'scan',
    title: 'Nightly scan',
    blurb: 'The pipeline refreshes itself without anyone pressing anything. These two decide when.',
    keys: ['scanCadence', 'scanHourUtc'],
  },
  {
    id: 'live',
    title: 'Live sync',
    blurb: 'What happens between the nightly passes: mail and calendar changes arriving by webhook, and the fallback poll that catches whatever a webhook missed.',
    keys: ['liveSyncMinutes', 'changeNotifications'],
  },
  {
    id: 'signals',
    title: 'Signals & competitors',
    blurb: 'Who the firm sells against, and how recent a two-way conversation has to be before an import stops on it.',
    keys: ['competitors', 'bilateralRecentDays'],
  },
  {
    id: 'access',
    title: 'Sign-in',
    blurb: 'How people get in, and the wiring chosen when Scout was deployed.',
    keys: ['signInIdentifier', 'modelProvider'],
  },
]

/**
 * The fields grouped for reading. A field the pack adds that no section claims lands in "Other
 * settings" rather than disappearing — the manifest is the form, so the screen shows all of it.
 */
export function sectionsOf(fields: SettingField[]): SettingSection[] {
  const claimed = new Set(SECTIONS.flatMap((s) => s.keys))
  const sections = SECTIONS.map((s) => ({
    id: s.id,
    title: s.title,
    blurb: s.blurb,
    fields: s.keys.map((k) => fields.find((f) => f.key === k)).filter((f): f is SettingField => f !== undefined),
  })).filter((s) => s.fields.length > 0)
  const rest = fields.filter((f) => !claimed.has(f.key))
  return rest.length === 0
    ? sections
    : [...sections, { id: 'other', title: 'Other settings', blurb: 'Added to the pack since this screen was written.', fields: rest }]
}

/**
 * The bounds the API will accept for a number, as a hint beside the input. The authority is
 * Scout.Domain DeploymentBounds — the same table the configuration binder uses — and a value outside
 * it comes back as a refusal with the true range, which is what the screen then shows.
 */
export const BOUNDS: Record<string, { min: number; max: number }> = {
  lookbackMonths: { min: 0, max: 600 },
  scanHourUtc: { min: 0, max: 23 },
  bilateralRecentDays: { min: 1, max: 3650 },
  liveSyncMinutes: { min: 0, max: 1440 },
}

/** How a list-shaped setting is written as one string: ';' for the org pairs, ',' for the rest. */
export function separatorOf(key: string): string {
  return key === 'orgAliases' || key === 'orgNames' ? ';' : ','
}

/** A text setting that is a list of `left=right` pairs rather than a list of plain values. */
export function isPairList(key: string): boolean {
  return key === 'orgAliases' || key === 'orgNames'
}

/** The column headings for a pair list, so the editor says what each side means. */
export function pairHeadings(key: string): [string, string] {
  return key === 'orgAliases' ? ['This domain', 'is the same org as'] : ['This org', 'is called']
}

export interface Pair {
  left: string
  right: string
}

/**
 * The entries as TYPED, empties kept. The editor needs this: a row somebody just added is empty, and
 * it has to survive on the screen until they fill it in. Saving goes through `tidy`, which drops them.
 */
export function rowsOf(value: string, key: string): string[] {
  return value.length === 0 ? [] : value.split(separatorOf(key)).map((v) => v.trim())
}

/** What is actually saved: the rows, emptied of blanks, written back as one string. */
export function tidy(value: string, key: string): string {
  return isPairList(key) ? fromPairs(toPairs(value, key), key) : join(split(value, key), key)
}

/** "a=b;c=d" → rows. A malformed entry keeps its text on the left so nothing is silently dropped. */
export function toPairs(value: string, key: string): Pair[] {
  return rowsOf(value, key).map((entry) => {
    const at = entry.indexOf('=')
    return at < 0 ? { left: entry.trim(), right: '' } : { left: entry.slice(0, at).trim(), right: entry.slice(at + 1).trim() }
  })
}

/** Rows → "a=b;c=d". An empty row on either side is dropped rather than written as half a pair. */
export function fromPairs(pairs: Pair[], key: string): string {
  return pairs.filter((p) => p.left.trim() && p.right.trim()).map((p) => `${p.left.trim()}=${p.right.trim()}`).join(separatorOf(key))
}

/** "a,b" → ['a', 'b'], on whichever separator this setting uses. */
export function split(value: string, key: string): string[] {
  return value.split(separatorOf(key)).map((v) => v.trim()).filter((v) => v.length > 0)
}

export function join(values: string[], key: string): string {
  return values.map((v) => v.trim()).filter((v) => v.length > 0).join(separatorOf(key))
}

/** The badge beside a field: who chose this value. */
export function sourceWords(field: SettingField): string {
  if (field.source === 'stored') {
    const when = field.updatedAt ? new Date(field.updatedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null
    return field.updatedBy ? `set by ${field.updatedBy}${when ? ` on ${when}` : ''}` : 'set here'
  }
  return field.source === 'config' ? 'deployed' : 'default'
}

/** What happens when this one is saved. Said in full, not as a word only the code understands. */
export function effectWords(effect: SettingEffect): string {
  switch (effect) {
    case 'next-run':
      return 'Takes effect at the next nightly run.'
    case 'next-sign-in':
      return 'Takes effect at the next sign-in.'
    default:
      return 'Takes effect immediately.'
  }
}

/** How many settings this firm has changed since the deploy. */
export function overriddenCount(fields: SettingField[]): number {
  return fields.filter((f) => f.source === 'stored').length
}

export function sourceClass(source: SettingSource): string {
  return `setting-source ${source}`
}
